<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Plus, Delete, ZoomIn } from '@element-plus/icons-vue'
import { createImageUploadHandler, getImageAcceptTypes, getImageUploadTip } from '@/utils/uploadUtils.js'
import { getImgUrl } from '@/utils/image.js'

// 定义组件 Props
interface Props {
    modelValue?: string // v-model 绑定的图片 key
    category?: string // 上传分类，默认 'site'
    size?: 'small' | 'medium' | 'large' | 'custom' // 预设尺寸
    width?: string | number // 自定义宽度（当 size 为 'custom' 时使用）
    height?: string | number // 自定义高度（当 size 为 'custom' 时使用）
    disabled?: boolean // 是否禁用
    showPreview?: boolean // 是否显示预览功能
    showDelete?: boolean // 是否显示删除按钮
    placeholder?: string // 占位符文本
    tip?: string // 提示文本
    maxSize?: number // 最大文件大小（MB）
}

const props = withDefaults(defineProps<Props>(), {
    category: 'site',
    size: 'small',
    disabled: false,
    showPreview: true,
    showDelete: true,
    placeholder: '',
    maxSize: 5
})

// 定义 Emits
const emit = defineEmits<{
    'update:modelValue': [value: string]
    'success': [key: string]
    'error': [error: any]
    'delete': []
}>()

// 上传状态
const uploading = ref(false)

// 计算预设尺寸样式
const sizeStyles = computed(() => {
    const sizeMap = {
        small: { width: '60px', height: '60px' },
        medium: { width: '100px', height: '100px' },
        large: { width: '150px', height: '150px' },
        custom: {
            width: typeof props.width === 'number' ? `${props.width}px` : props.width || '100px',
            height: typeof props.height === 'number' ? `${props.height}px` : props.height || '100px'
        }
    }
    return sizeMap[props.size]
})

// 计算提示文本
const uploadTip = computed(() => {
    return props.tip || getImageUploadTip(props.maxSize)
})

// 创建上传处理函数
const uploadHandler = createImageUploadHandler(
    (imageKey: string) => {
        emit('update:modelValue', imageKey)
        emit('success', imageKey)
        uploading.value = false
        ElMessage.success('图片上传成功')
    },
    (error: any) => {
        emit('error', error)
        uploading.value = false
    },
    props.category
)

// 处理上传
const handleUpload = (options: any) => {
    uploading.value = true
    return uploadHandler(options)
}

// 删除图片
const handleDelete = () => {
    emit('update:modelValue', '')
    emit('delete')
    ElMessage.success('图片已删除')
}

// 预览图片列表
const previewList = computed(() => {
    return props.modelValue ? [getImgUrl(props.modelValue)] : []
})
</script>

<template>
    <div class="image-upload-container">
        <el-upload class="image-upload" :class="{
            'is-disabled': disabled,
        }" :http-request="handleUpload" :accept="getImageAcceptTypes()" :show-file-list="false"
            :disabled="disabled || uploading">
            <div v-if="modelValue" class="upload-image-wrapper" :style="sizeStyles">
                <el-image :src="getImgUrl(modelValue)" :preview-src-list="showPreview ? previewList : undefined"
                    :preview-teleported="true" fit="cover" class="uploaded-image" />

                <div class="image-actions" v-if="!disabled">
                    <el-icon v-if="showDelete" class="action-icon delete-icon" @click.stop="handleDelete">
                        <Delete />
                    </el-icon>
                </div>
               
                <div v-if="uploading" class="upload-loading">
                    <el-icon class="is-loading">
                        <Plus />
                    </el-icon>
                </div>
            </div>

            <div v-else class="upload-placeholder" :style="sizeStyles">
                <el-icon v-if="!uploading" class="upload-icon">
                    <Plus />
                </el-icon>
                <el-icon v-else class="upload-icon is-loading">
                    <Plus />
                </el-icon>
                <div class="upload-text">{{ uploading ? '上传中...' : placeholder }}</div>
            </div>
        </el-upload>

        <div v-if="uploadTip" class="upload-tip">
            {{ uploadTip }}
        </div>
    </div>
</template>

<style scoped>

.image-upload-container {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
}

.image-upload {
    display: block;
    width: auto;
    height: auto;
}

.image-upload :deep(.el-upload) {
    display: block;
}

.upload-image-wrapper {
    position: relative;
    border: 1px dashed var(--el-border-color);
    border-radius: 6px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s;
}

.upload-image-wrapper:hover {
    border-color: var(--el-color-primary);
}

.upload-image-wrapper:hover .image-actions {
    opacity: 1;
}

.uploaded-image {
    width: 100%;
    height: 100%;
    display: block;
}

.image-actions {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    opacity: 0;
    transition: opacity 0.3s;
}

.action-icon {
    color: white;
    font-size: 30px;
    cursor: pointer;
    padding: 4px;
    border-radius: 2px;
    transition: background-color 0.3s;
}

.action-icon:hover {
    background-color: rgba(255, 255, 255, 0.2);
}

.preview-icon:hover {
    color: var(--el-color-primary);
}

.delete-icon:hover {
    color: var(--el-color-danger);
}

.upload-placeholder {
    border: 1px dashed var(--el-border-color);
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s;
    background-color: var(--el-fill-color-lighter);
}

.upload-placeholder:hover {
    border-color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

.is-disabled .upload-placeholder {
    cursor: not-allowed;
    background-color: var(--el-disabled-bg-color);
    border-color: var(--el-disabled-border-color);
}

.is-disabled .upload-placeholder:hover {
    border-color: var(--el-disabled-border-color);
    background-color: var(--el-disabled-bg-color);
}

.upload-icon {
    font-size: 24px;
    color: var(--el-text-color-secondary);
}

.is-disabled .upload-icon {
    color: var(--el-disabled-text-color);
}

.upload-text {
    font-size: 12px;
    color: var(--el-text-color-regular);
    text-align: center;
}

.is-disabled .upload-text {
    color: var(--el-disabled-text-color);
}

.upload-loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
}

.upload-tip {
    margin-top: 8px;
    font-size: 10px;
    color: var(--el-text-color-secondary);
    text-align: left;
    line-height: 1.5;
}

.is-loading {
    animation: rotating 2s linear infinite;
}

@keyframes rotating {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>